import * as React from 'react';
import { memo, useEffect, useState } from 'react';
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button,
  makeStyles,
  Checkbox,
  Switch,
} from '@material-ui/core';

import { User, UserModel } from '../../models/UserModel';
import { LocalStorageService } from '../../services/LocalStorageService.';
import { set } from 'date-fns';
import NotificationToast from '../common/NotificationToast';
import { Spinner } from 'react-bootstrap';

const useStyles = makeStyles(theme => ({
  tableContainer: {
    marginTop: theme.spacing(2),
    borderRadius: theme.shape.borderRadius,
    boxShadow: theme.shadows[3],
    height: '80vh',
    overflowY: 'auto',
  },
  headerCell: {
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.common.white,
    fontWeight: 'bold',
  },
  addButton: {
    // display: "flex",
    // alignItems: "center",
    color: theme.palette.primary.main,
    cursor: 'pointer',
    fontWeight: 'bold',
    '&:hover': {
      textDecoration: 'underline',
    },
  },
  removeButton: {
    color: theme.palette.error.main,
    '&:hover': {
      backgroundColor: theme.palette.error.light,
    },
  },
  tableRow: {
    '&:nth-of-type(odd)': {
      backgroundColor: theme.palette.action.hover,
    },
  },
  registerButton: {
    marginTop: theme.spacing(2),
    backgroundColor: theme.palette.success.main,
    color: theme.palette.common.white,
    '&:hover': {
      backgroundColor: theme.palette.success.dark,
    },
  },
}));

interface DailyReportSelectMemberProps {
  onSave: (userIds: number[]) => void;
}

/**
 * 報告先(社内)メンバー設定テーブル
 * @param props
 * @returns
 */
const DailyReportSelectMember = (props: DailyReportSelectMemberProps) => {
  const classes = useStyles();
  const { onSave } = props;
  const [users, setUsers] = useState<User[]>([]);
  // const [myUser, setMyUser] = useState<User>();
  const [selectedUserIds, setSelectedUserIds] = useState<number[]>([]);
  const [isPublic, setIsPublic] = useState<boolean>();
  const [isPublicLoading, setIsPublicLoading] = useState<boolean>(true);

  const myUserId = LocalStorageService.getUser()?.id;

  /**
   * 自分の会社のメンバーを取得する
   */
  const fetchUsers = async () => {
    const result = await UserModel.fetchMyCompanyUsers();
    setUsers(result);
    //自分の情報を抽出する
    const myUser = result.find(user => user.id === myUserId);
    setIsPublic(myUser?.is_public_report || false);
    setIsPublicLoading(false);

    // 自分の会社のメンバーから、日報メンバーに含まれているユーザーを抽出
    // filter()メソッドで、daily_reports_membersプロパティがtrueのユーザーを抽出
    // その結果を、memberUsersというStateに格納
    const memberUsers = result.filter(
      user => user.daily_reports_members && user.daily_reports_members.length > 0
    );

    setSelectedUserIds(memberUsers.map(user => user.id));
  };

  const onRemove = (user: User) => {
    setSelectedUserIds(selectedUserIds.filter(e => e !== user.id));
  };

  const onAdd = (user: User) => {
    setSelectedUserIds([...selectedUserIds, user.id]);
  };

  const onhandleChangePublic = async () => {
    setIsPublicLoading(true);
    try {
      await UserModel.changeIsPublicReport({ is_public_report: !isPublic });
      setIsPublic(!isPublic);
      // NotificationToast().success('変更しました');
    } catch (error) {
      NotificationToast().success('システムエラーが発生しました。');
    }
    setIsPublicLoading(false);
  };

  useEffect(() => {
    fetchUsers();
  }, []);

  return (
    <Box>
      <TableContainer component={Paper} className={classes.tableContainer}>
        <Table>
          <TableHead style={{ position: 'sticky', top: 0, backgroundColor: 'white', zIndex: 999 }}>
            <TableRow>
              <TableCell className={classes.headerCell} style={{ minWidth: 150 }} align="center">
                {`報告する:${selectedUserIds.length}人`}
              </TableCell>
              <TableCell className={classes.headerCell} style={{ minWidth: 150 }}>
                名前
              </TableCell>
              <TableCell className={classes.headerCell} style={{ minWidth: 200 }}>
                受信アドレス
              </TableCell>
              <TableCell className={classes.headerCell} style={{ minWidth: 180 }}>
                アドレス
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {users.map(user => (
              <TableRow
                key={user.id}
                className={classes.tableRow}
                style={{
                  backgroundColor: selectedUserIds.includes(user.id) ? '#f3f3ff' : 'white',
                }}
              >
                <TableCell align="center">
                  <Checkbox
                    checked={selectedUserIds.includes(user.id)}
                    onChange={() => {
                      if (selectedUserIds.includes(user.id)) {
                        setSelectedUserIds(selectedUserIds.filter(id => id !== user.id));
                      } else {
                        setSelectedUserIds([...selectedUserIds, user.id]);
                      }
                    }}
                    color="primary"
                  />
                </TableCell>
                <TableCell>{user.name}</TableCell>
                <TableCell>{user.destination_email || user.email}</TableCell>
                <TableCell>{user.email}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      <Box display="flex" justifyContent="center" mt={2}>
        <Box>
          <label style={{ marginRight: 8 }}> 【社内全員】であなたの日報を公開しますか?</label>
          {!isPublicLoading && (
            <Checkbox
              checked={isPublic}
              onChange={onhandleChangePublic}
              value="public"
              color="primary"
            />
          )}

          {isPublicLoading && <Spinner color="primary" animation="border" size="sm" />}
        </Box>
      </Box>
      <Box display="flex" justifyContent="center">
        <Button
          color="primary"
          variant="contained"
          className={classes.registerButton}
          onClick={() => onSave(selectedUserIds)}
        >
          確定
        </Button>
      </Box>
    </Box>
  );
};

export default DailyReportSelectMember;
